import { icon } from './icons'

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl'

export type ModalOpenOptions = {
  size?: ModalSize
  title: string
  eyebrow?: string
  body: string
  footer?: string
  on_close?: () => void
}

const ROOT_ID = 'app-modal-root'
const BASE_Z = 50
const Z_STEP = 10

type Layer = {
  overlay: HTMLElement
  close: () => void
}

const layers: Layer[] = []
let escape_bound = false

function ensure_root(): HTMLElement {
  let root = document.getElementById(ROOT_ID)
  if (root) return root
  root = document.createElement('div')
  root.id = ROOT_ID
  document.body.appendChild(root)
  return root
}

function size_class(size: ModalSize | undefined): string {
  if (size === 'sm') return 'max-w-md'
  if (size === 'lg') return 'max-w-3xl'
  if (size === 'xl') return 'max-w-5xl'
  return 'max-w-2xl'
}

function is_public_paper(): boolean {
  return Boolean(document.querySelector('.public-paper'))
}

function on_escape(event: KeyboardEvent) {
  if (event.key !== 'Escape') return
  if (!layers.length) return
  event.preventDefault()
  event.stopPropagation()
  layers[layers.length - 1].close()
}

function ensure_escape() {
  if (escape_bound) return
  escape_bound = true
  document.addEventListener('keydown', on_escape)
}

function release_escape_if_idle() {
  if (layers.length || !escape_bound) return
  document.removeEventListener('keydown', on_escape)
  escape_bound = false
}

export type ModalHandle = {
  close: () => void
  root: HTMLElement
  body: HTMLElement
  footer: HTMLElement
}

export function open_modal(opts: ModalOpenOptions): ModalHandle {
  const root = ensure_root()
  const overlay = document.createElement('div')
  overlay.className = 'fixed inset-0 flex items-end justify-center bg-canvas/70 backdrop-blur-sm p-3 sm:items-center'
  overlay.style.zIndex = String(BASE_Z + layers.length * Z_STEP)
  overlay.setAttribute('role', 'dialog')
  overlay.setAttribute('aria-modal', 'true')
  overlay.setAttribute('data-modal-layer', String(layers.length))

  const panel = document.createElement('div')
  panel.className = `relative w-full ${size_class(opts.size)} rounded-lg border border-border bg-surface text-ink shadow-xl max-h-[92vh] overflow-hidden flex flex-col`
  if (is_public_paper()) panel.classList.add('public-newsprint-modal')

  const header = document.createElement('div')
  header.className = 'flex items-start justify-between gap-3 border-b border-border px-5 py-4'
  header.innerHTML = `
    <div class="min-w-0">
      ${opts.eyebrow ? `<div class="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted">${opts.eyebrow}</div>` : ''}
      <h2 class="truncate text-lg font-semibold text-ink">${opts.title}</h2>
    </div>
    <button type="button" class="js-modal-close inline-flex h-9 w-9 items-center justify-center rounded-md border border-border text-ink hover:bg-surface2" aria-label="Fechar">
      ${icon('x', 'h-4 w-4')}
    </button>`

  const body = document.createElement('div')
  body.className = 'flex-1 overflow-auto px-5 py-4'
  body.innerHTML = opts.body

  const footer = document.createElement('div')
  footer.className = 'border-t border-border bg-surface2/60 px-5 py-3'
  footer.innerHTML = opts.footer || ''

  panel.appendChild(header)
  panel.appendChild(body)
  panel.appendChild(footer)
  overlay.appendChild(panel)

  const parent = layers[layers.length - 1]
  if (parent) parent.overlay.setAttribute('inert', '')

  const previously_focused = document.activeElement instanceof HTMLElement ? document.activeElement : null
  root.appendChild(overlay)

  let closed = false
  const close = () => {
    if (closed) return
    closed = true
    const idx = layers.findIndex(layer => layer.overlay === overlay)
    if (idx >= 0) {
      while (layers.length - 1 > idx) layers[layers.length - 1].close()
      layers.splice(idx, 1)
    }
    overlay.remove()
    const next = layers[layers.length - 1]
    if (next) next.overlay.removeAttribute('inert')
    release_escape_if_idle()
    if (previously_focused?.isConnected) previously_focused.focus()
    opts.on_close?.()
  }

  layers.push({ overlay, close })
  ensure_escape()

  header.querySelector('.js-modal-close')?.addEventListener('click', (event) => {
    event.preventDefault()
    event.stopPropagation()
    close()
  })
  overlay.addEventListener('pointerdown', (event) => {
    if (event.target === overlay) close()
  })

  return { close, root: panel, body, footer }
}

export function close_modal(): void {
  layers[layers.length - 1]?.close()
}
